"use client";

// ============================================================
// CLUBHUB – Dokumente: was rechts neben dem Baum steht
// ============================================================
// Raster oder Liste — dieselben Dateien, dieselben Handgriffe.
// Die Kachel ist die Gestalt aus dem DAM (`ui/file-tile`): Vorschau
// 4:3, Name, eine Zeile Auskunft, Kaestchen und „…“ in den Ecken.
// Die Liste ist die Listentabelle des Hauses; auf dem Telefon
// faellt sie auf Name plus eine Zeile Auskunft zusammen.
//
// Unterordner stehen VOR den Dateien und sind selbst Ziele: eine
// Datei darauf gezogen wandert hinein, eine Datei vom Rechner
// darauf gezogen wird dort hochgeladen. So kommt man auf dem
// Telefon ohne Baum aus — der Ordner ist, wo man hintippt.
//
// Ganz ohne Ordner (Startseite) stehen die drei Laufwerke als
// Karten mit dem Satz, wer sie sieht, darunter was zuletzt kam.
// ============================================================

import { Fragment, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ChevronRight,
  Download,
  Eye,
  FileStack,
  Folder,
  FolderInput,
  FolderPlus,
  Link2,
  MoreHorizontal,
  Pencil,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { confirmDialog } from "@/components/ui/confirm";
import { DropdownItem, DropdownMenu, DropdownSeparator } from "@/components/ui/dropdown-menu";
import {
  FILE_TILE_CLASS,
  FILE_TILE_CORNER_BUTTON,
  FileTileCaption,
  FileTilePreview,
  fileTileState,
} from "@/components/ui/file-tile";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MehrKnopf } from "@/components/ui/zeichen-knopf";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/toast";
import { Listentabelle, MarkierKopf, MarkierZelle, ZEILENGRUND, type useMarkierung } from "@/components/liste";
import { cn, formatDate } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import {
  DATEIARTEN,
  dateiArt,
  formatBytes,
  freierName,
  kinderVon,
  linkStatus,
  nachkommen,
  pfadText,
  pfadZu,
  sehenDuerfen,
  wirksameSicht,
  zeitwert,
} from "@/lib/dokumente/format";
import type { DocumentView, FolderView } from "@/lib/dokumente/views";
import { SICHT_ZEICHEN } from "./dialoge";
import { useAblage } from "./ablage-kontext";
import { Daumen, Kachelvorschau } from "./vorschau";
import { dateienVerschieben } from "@/app/(app)/dokumente/actions";
import { errorOf } from "@/lib/action-result";

type Markierung = ReturnType<typeof useMarkierung>;

/** Kennung im Ziehen: welche Dateien gerade wandern. */
export const ZIEH_TYP = "application/x-clubhub-dokumente";

function ziehen(e: React.DragEvent, ids: string[]) {
  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData(ZIEH_TYP, JSON.stringify(ids));
}

/**
 * Ein Ordner als Ablageziel — fuer gezogene Dateien aus der Ablage und
 * vom Rechner. Verschoben wird nach denselben Regeln wie im Dialog:
 * belegte Namen bekommen einen Zusatz, Fotos in einen oeffentlichen
 * Ordner nur nach Rueckfrage. `data-ablageziel` sagt der Flaeche
 * darunter, dass sie ihren Rahmen zuruecknehmen soll.
 */
export function useAblageziel(folderId: string) {
  const { aendern, hochladen, bestand, daten } = useAblage();
  const router = useRouter();
  const [ueber, setUeber] = useState(false);

  async function verschieben(ids: string[]) {
    if (!daten.darfSchreiben) return;
    const dateien = bestand.documents.filter((d) => ids.includes(d.id) && d.folderId !== folderId);
    if (dateien.length === 0 || !bestand.folders.some((f) => f.id === folderId)) return;
    const ziel = wirksameSicht(folderId, bestand.folders);
    const bilder = dateien.filter((d) => dateiArt(d.mimeType, d.name) === "bild" && wirksameSicht(d.folderId, bestand.folders).visibility !== "PUBLIC").length;
    if (ziel.visibility === "PUBLIC" && bilder > 0) {
      const weiter = await confirmDialog({
        title: bilder === 1 ? "Ein Foto öffentlich machen?" : `${formatDecimal(bilder)} Fotos öffentlich machen?`,
        description: `In „${pfadText(folderId, bestand.folders)}“ sieht sie jeder, auch auf der Website. ${formatDecimal(daten.ohneFotoEinwilligung)} Mitglieder haben Fotos im Internet nicht zugestimmt.`,
        confirmLabel: "Trotzdem verschieben",
      });
      if (!weiter) return;
    }
    const belegt = bestand.documents.filter((d) => d.folderId === folderId).map((d) => d.name);
    const namen = new Map<string, string>();
    for (const d of dateien) {
      const n = freierName(d.name, belegt);
      belegt.push(n);
      namen.set(d.id, n);
    }
    // Wie im Dialog, mit derselben Namensregel — der Server zuerst.
    const fehler = errorOf(await dateienVerschieben(dateien.map((d) => d.id), folderId));
    if (fehler) {
      toast.error(fehler);
      return;
    }
    if (daten.quelle === "datenbank") router.refresh();
    aendern((b) => ({
      ...b,
      documents: b.documents.map((d) => (namen.has(d.id) ? { ...d, folderId, name: namen.get(d.id)! } : d)),
    }));
    const umbenannt = dateien.filter((d) => namen.get(d.id) !== d.name).length;
    toast.success(`${dateien.length === 1 ? "Datei" : `${formatDecimal(dateien.length)} Dateien`} verschoben`, {
      description: `${pfadText(folderId, bestand.folders)}${umbenannt > 0 ? ` · ${formatDecimal(umbenannt)} mit Zusatz im Namen, weil es ihn dort schon gab` : ""}`,
    });
  }

  return {
    ueber,
    ziel: {
      "data-ablageziel": "",
      onDragOver: (e: React.DragEvent) => {
        if (!daten.darfSchreiben) return;
        const typen = Array.from(e.dataTransfer.types);
        if (!typen.includes(ZIEH_TYP) && !typen.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
        setUeber(true);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setUeber(false);
      },
      onDrop: (e: React.DragEvent) => {
        setUeber(false);
        if (!daten.darfSchreiben) return;
        const roh = e.dataTransfer.getData(ZIEH_TYP);
        if (roh) {
          e.preventDefault();
          e.stopPropagation();
          void verschieben(JSON.parse(roh) as string[]);
          return;
        }
        if (e.dataTransfer.files.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          hochladen(e.dataTransfer.files, folderId);
        }
      },
    },
  };
}

// ── Menues ───────────────────────────────────────────────────
export function DateiMenue({ datei, trigger }: { datei: DocumentView; trigger: React.ReactNode }) {
  const { daten, setzen, oeffnen, herunterladen, dateienLoeschen } = useAblage();
  return (
    <DropdownMenu trigger={trigger}>
      <DropdownItem onClick={() => setzen({ datei: datei.id }, true)}>
        <Eye className="h-4 w-4" aria-hidden />
        Öffnen
      </DropdownItem>
      <DropdownItem onClick={() => herunterladen([datei.id])}>
        <Download className="h-4 w-4" aria-hidden />
        Herunterladen
      </DropdownItem>
      {daten.darfSchreiben && (
      <>
      <DropdownSeparator />
      <DropdownItem onClick={() => oeffnen({ art: "datei-umbenennen", dateiId: datei.id })}>
        <Pencil className="h-4 w-4" aria-hidden />
        Umbenennen…
      </DropdownItem>
      <DropdownItem onClick={() => oeffnen({ art: "dateien-verschieben", dateiIds: [datei.id] })}>
        <FolderInput className="h-4 w-4" aria-hidden />
        Verschieben…
      </DropdownItem>
      <DropdownItem onClick={() => oeffnen({ art: "teilen", dateiId: datei.id })}>
        <Link2 className="h-4 w-4" aria-hidden />
        Link teilen…
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem variant="danger" onClick={() => void dateienLoeschen([datei.id])}>
        <Trash2 className="h-4 w-4" aria-hidden />
        Löschen
      </DropdownItem>
      </>
      )}
    </DropdownMenu>
  );
}

export function OrdnerMenue({ ordner, trigger }: { ordner: FolderView; trigger: React.ReactNode }) {
  const { daten, oeffnen, zumOrdner, ordnerLoeschen, ordnerAlsZip } = useAblage();
  const laufwerk = ordner.parentId === null;
  const schreiben = daten.darfSchreiben;
  return (
    <DropdownMenu trigger={trigger}>
      <DropdownItem onClick={() => zumOrdner(ordner.id)}>
        <Eye className="h-4 w-4" aria-hidden />
        Öffnen
      </DropdownItem>
      {schreiben && (
        <DropdownItem onClick={() => oeffnen({ art: "ordner-neu", parentId: ordner.id })}>
          <FolderPlus className="h-4 w-4" aria-hidden />
          Neuer Unterordner…
        </DropdownItem>
      )}
      <DropdownItem onClick={() => oeffnen({ art: "ordner-rechte", folderId: ordner.id })}>
        <ShieldCheck className="h-4 w-4" aria-hidden />
        {laufwerk || !schreiben ? "Rechte ansehen" : "Rechte…"}
      </DropdownItem>
      <DropdownItem onClick={() => ordnerAlsZip(ordner.id)}>
        <Download className="h-4 w-4" aria-hidden />
        Als ZIP laden
      </DropdownItem>
      {!laufwerk && schreiben && (
        <>
          <DropdownSeparator />
          <DropdownItem onClick={() => oeffnen({ art: "ordner-umbenennen", folderId: ordner.id })}>
            <Pencil className="h-4 w-4" aria-hidden />
            Umbenennen…
          </DropdownItem>
          <DropdownItem onClick={() => oeffnen({ art: "ordner-verschieben", folderId: ordner.id })}>
            <FolderInput className="h-4 w-4" aria-hidden />
            Verschieben…
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem variant="danger" onClick={() => void ordnerLoeschen(ordner.id)}>
            <Trash2 className="h-4 w-4" aria-hidden />
            Löschen
          </DropdownItem>
        </>
      )}
    </DropdownMenu>
  );
}

/** „PDF · 245 KB“ — die eine Zeile unter dem Namen. */
function auskunft(d: DocumentView, mitDatum = false): string {
  const teile = [DATEIARTEN[dateiArt(d.mimeType, d.name)].label, formatBytes(d.size)];
  if (mitDatum) teile.push(formatDate(d.updatedAt));
  return teile.join(" · ");
}

// ── Kopf ueber dem Inhalt ────────────────────────────────────
export function OrdnerKopf({ ordner }: { ordner: FolderView }) {
  const { bestand, daten, zumOrdner, oeffnen } = useAblage();
  const weg = pfadZu(ordner.id, bestand.folders);
  const sicht = wirksameSicht(ordner.id, bestand.folders);
  const aussage = sehenDuerfen(sicht, daten);
  const Zeichen = SICHT_ZEICHEN[sicht.visibility];

  return (
    <div className="border-border-subtle bg-bg-primary flex items-center gap-2 border-b py-2 pr-2 pl-4">
      <nav aria-label="Ordnerpfad" className="text-body-sm flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button type="button" onClick={() => zumOrdner(null)} className="text-text-tertiary hover:text-text-primary shrink-0 transition-colors">
          Laufwerke
        </button>
        {weg.map((f, i) => (
          <Fragment key={f.id}>
            <ChevronRight className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
            {i === weg.length - 1 ? (
              <span className="text-text-primary shrink-0 font-medium" aria-current="page">
                {f.name}
              </span>
            ) : (
              <button type="button" onClick={() => zumOrdner(f.id)} className="text-text-tertiary hover:text-text-primary shrink-0 transition-colors">
                {f.name}
              </button>
            )}
          </Fragment>
        ))}
      </nav>
      <button
        type="button"
        onClick={() => oeffnen({ art: "ordner-rechte", folderId: ordner.id })}
        title={`Sehen dürfen: ${aussage.satz}`}
        className="text-caption text-text-secondary bg-bg-secondary hover:bg-bg-tertiary duration-fast flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 transition-colors"
      >
        <Zeichen className="h-3.5 w-3.5" aria-hidden />
        <span className="max-sm:hidden">Sehen:</span>
        <span className="max-w-[10rem] truncate font-medium">{aussage.kurz}</span>
      </button>
      <OrdnerMenue ordner={ordner} trigger={<MehrKnopf groesse="mittel" label={`${ordner.name}: weitere Aktionen`} />} />
    </div>
  );
}

// ── Raster ───────────────────────────────────────────────────
function OrdnerKachel({ ordner }: { ordner: FolderView }) {
  const { zumOrdner, zahlen, bestand } = useAblage();
  const { ueber, ziel } = useAblageziel(ordner.id);
  const z = zahlen.get(ordner.id);
  const Zeichen = SICHT_ZEICHEN[wirksameSicht(ordner.id, bestand.folders).visibility];
  return (
    <li>
      <div {...ziel} className={cn(FILE_TILE_CLASS, fileTileState(ueber))}>
        <button type="button" onClick={() => zumOrdner(ordner.id)} className="block w-full text-left" title={ordner.name}>
          <FileTilePreview className="bg-bg-tertiary/60">
            <span className="absolute inset-0 grid place-items-center">
              <Folder className="text-text-tertiary h-10 w-10" strokeWidth={1.4} aria-hidden />
            </span>
            <span className="bg-bg-elevated/90 text-caption text-text-secondary absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-md px-1.5 py-0.5">
              <Zeichen className="h-3 w-3" aria-hidden />
            </span>
          </FileTilePreview>
          <FileTileCaption
            title={ordner.name}
            meta={<span className="tabular-nums">{z ? `${formatDecimal(z.dateien)} ${z.dateien === 1 ? "Datei" : "Dateien"}` : "leer"}</span>}
          />
        </button>
        <div className="absolute top-1.5 right-1.5">
          <OrdnerMenue
            ordner={ordner}
            trigger={
              <button type="button" aria-label={`${ordner.name}: weitere Aktionen`} className={FILE_TILE_CORNER_BUTTON}>
                <MehrZeichen />
              </button>
            }
          />
        </div>
      </div>
    </li>
  );
}

function MehrZeichen() {
  return <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />;
}

function DateiKachel({ datei, markierung, mitPfad }: { datei: DocumentView; markierung: Markierung; mitPfad: boolean }) {
  const { setzen, bestand, daten } = useAblage();
  const gewaehlt = markierung.istMarkiert(datei.id);
  const geteilt = datei.shares.some((l) => linkStatus(l, daten.today) === "aktiv");
  return (
    <li>
      <div
        draggable={daten.darfSchreiben}
        onDragStart={(e) => ziehen(e, gewaehlt ? markierung.markiert : [datei.id])}
        className={cn(FILE_TILE_CLASS, fileTileState(gewaehlt))}
      >
        <button type="button" onClick={() => setzen({ datei: datei.id }, true)} className="block w-full text-left" title={datei.name}>
          <FileTilePreview>
            <Kachelvorschau datei={datei} />
            {(datei.version > 1 || geteilt) && (
              <span className="absolute right-1.5 bottom-1.5 flex items-center gap-1">
                {geteilt && (
                  <span className="bg-bg-elevated/90 text-text-secondary grid h-5 w-5 place-items-center rounded-md" title="Mit Link geteilt">
                    <Link2 className="h-3 w-3" aria-hidden />
                  </span>
                )}
                {datei.version > 1 && (
                  <span className="bg-bg-elevated/90 text-caption text-text-secondary rounded-md px-1.5 py-0.5 tabular-nums" title={`Version ${datei.version}`}>
                    v{datei.version}
                  </span>
                )}
              </span>
            )}
          </FileTilePreview>
          <FileTileCaption
            title={datei.name}
            meta={<span className="tabular-nums">{mitPfad ? pfadText(datei.folderId, bestand.folders) : auskunft(datei)}</span>}
          />
        </button>
        <div
          className={cn(
            "duration-fast absolute top-1.5 left-1.5 transition-opacity",
            gewaehlt || markierung.markiert.length > 0 ? "opacity-100" : "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
          )}
        >
          <Checkbox
            checked={gewaehlt}
            onChange={() => {}}
            onClick={(e) => {
              e.stopPropagation();
              markierung.umschalten(datei.id, e.shiftKey);
            }}
            aria-label={gewaehlt ? `${datei.name} abwählen` : `${datei.name} wählen`}
            className="bg-bg-primary shadow-e1"
          />
        </div>
        <div className="absolute top-1.5 right-1.5">
          <DateiMenue
            datei={datei}
            trigger={
              <button type="button" aria-label={`${datei.name}: weitere Aktionen`} className={FILE_TILE_CORNER_BUTTON}>
                <MehrZeichen />
              </button>
            }
          />
        </div>
      </div>
    </li>
  );
}

export function DateiRaster({
  ordnerListe,
  dateien,
  markierung,
  mitPfad = false,
}: {
  ordnerListe: FolderView[];
  dateien: DocumentView[];
  markierung: Markierung;
  mitPfad?: boolean;
}) {
  return (
    <ul className="grid [grid-template-columns:repeat(auto-fill,minmax(148px,1fr))] gap-3 sm:[grid-template-columns:repeat(auto-fill,minmax(168px,1fr))]">
      {ordnerListe.map((f) => (
        <OrdnerKachel key={f.id} ordner={f} />
      ))}
      {dateien.map((d) => (
        <DateiKachel key={d.id} datei={d} markierung={markierung} mitPfad={mitPfad} />
      ))}
    </ul>
  );
}

// ── Liste ────────────────────────────────────────────────────
function OrdnerZeile({ ordner, mitPfad }: { ordner: FolderView; mitPfad: boolean }) {
  const { zumOrdner, zahlen, bestand } = useAblage();
  const { ueber, ziel } = useAblageziel(ordner.id);
  const z = zahlen.get(ordner.id);
  const Zeichen = SICHT_ZEICHEN[wirksameSicht(ordner.id, bestand.folders).visibility];
  return (
    <TableRow {...ziel} className={cn("cursor-pointer", ueber && ZEILENGRUND.markiert)}>
      <TableCell className="w-px pr-0" />
      <TableCell className="w-full max-w-0">
        <button
          type="button"
          onClick={() => zumOrdner(ordner.id)}
          className="flex w-full min-w-0 items-center gap-3 text-left after:absolute after:inset-0 after:content-['']"
        >
          <Daumen ordner />
          <span className="min-w-0">
            <span className="text-text-primary flex items-center gap-1.5 font-medium">
              <span className="truncate">{ordner.name}</span>
              <Zeichen className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
            </span>
            <span className="text-caption text-text-tertiary block truncate tabular-nums">
              {mitPfad
                ? pfadText(ordner.parentId, bestand.folders)
                : `${z ? `${formatDecimal(z.dateien)} ${z.dateien === 1 ? "Datei" : "Dateien"}` : "leer"}`}
              <span className="md:hidden">{z ? ` · ${formatBytes(z.groesse)}` : ""}</span>
            </span>
          </span>
        </button>
      </TableCell>
      <TableCell className="text-text-tertiary hidden md:table-cell">Ordner</TableCell>
      <TableCell className="text-text-secondary hidden text-right tabular-nums sm:table-cell">{z ? formatBytes(z.groesse) : "–"}</TableCell>
      <TableCell className="text-text-secondary hidden tabular-nums md:table-cell">{z ? formatDate(z.geaendert) : "–"}</TableCell>
      <TableCell className="text-text-tertiary hidden lg:table-cell">–</TableCell>
      <TableCell className="relative z-[1] w-px py-0">
        <OrdnerMenue ordner={ordner} trigger={<MehrKnopf groesse="klein" label={`${ordner.name}: weitere Aktionen`} />} />
      </TableCell>
    </TableRow>
  );
}

function DateiZeile({ datei, markierung, mitPfad }: { datei: DocumentView; markierung: Markierung; mitPfad: boolean }) {
  const { setzen, bestand, daten } = useAblage();
  const gewaehlt = markierung.istMarkiert(datei.id);
  const geteilt = datei.shares.some((l) => linkStatus(l, daten.today) === "aktiv");
  return (
    <TableRow
      draggable={daten.darfSchreiben}
      onDragStart={(e) => ziehen(e, gewaehlt ? markierung.markiert : [datei.id])}
      className={cn("cursor-pointer", gewaehlt && ZEILENGRUND.markiert)}
    >
      <MarkierZelle markierung={markierung} id={datei.id} label={datei.name} />
      <TableCell className="w-full max-w-0">
        <button
          type="button"
          onClick={() => setzen({ datei: datei.id }, true)}
          className="flex w-full min-w-0 items-center gap-3 text-left after:absolute after:inset-0 after:content-['']"
        >
          <Daumen datei={datei} />
          <span className="min-w-0">
            <span className="text-text-primary flex items-center gap-1.5 font-medium">
              <span className="truncate">{datei.name}</span>
              {geteilt && <Link2 className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-label="mit Link geteilt" />}
              {datei.version > 1 && <span className="text-caption text-text-tertiary shrink-0 font-normal tabular-nums">v{datei.version}</span>}
            </span>
            {mitPfad ? (
              <span className="text-caption text-text-tertiary block truncate">{pfadText(datei.folderId, bestand.folders)}</span>
            ) : (
              datei.description && <span className="text-caption text-text-tertiary block truncate max-md:hidden">{datei.description}</span>
            )}
            <span className="text-caption text-text-tertiary block truncate tabular-nums md:hidden">{auskunft(datei, true)}</span>
          </span>
        </button>
      </TableCell>
      <TableCell className="text-text-secondary hidden whitespace-nowrap md:table-cell">{DATEIARTEN[dateiArt(datei.mimeType, datei.name)].label}</TableCell>
      <TableCell className="text-text-secondary hidden text-right whitespace-nowrap tabular-nums sm:table-cell">{formatBytes(datei.size)}</TableCell>
      <TableCell className="text-text-secondary hidden tabular-nums md:table-cell">{formatDate(datei.updatedAt)}</TableCell>
      <TableCell className="text-text-secondary hidden whitespace-nowrap lg:table-cell">{datei.uploadedBy?.name ?? "–"}</TableCell>
      <TableCell className="relative z-[1] w-px py-0">
        <DateiMenue datei={datei} trigger={<MehrKnopf groesse="klein" label={`${datei.name}: weitere Aktionen`} />} />
      </TableCell>
    </TableRow>
  );
}

export function DateiListe({
  ordnerListe,
  dateien,
  markierung,
  mitPfad = false,
}: {
  ordnerListe: FolderView[];
  dateien: DocumentView[];
  markierung: Markierung;
  mitPfad?: boolean;
}) {
  return (
    <Listentabelle>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {dateien.length > 0 ? <MarkierKopf markierung={markierung} /> : <TableHead className="w-px pr-0" />}
          <TableHead>NAME</TableHead>
          <TableHead className="hidden md:table-cell">ART</TableHead>
          <TableHead className="hidden text-right sm:table-cell">GRÖSSE</TableHead>
          <TableHead className="hidden md:table-cell">GEÄNDERT</TableHead>
          <TableHead className="hidden lg:table-cell">VON</TableHead>
          <TableHead className="w-px">
            <span className="sr-only">Aktionen</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ordnerListe.map((f) => (
          <OrdnerZeile key={f.id} ordner={f} mitPfad={mitPfad} />
        ))}
        {dateien.map((d) => (
          <DateiZeile key={d.id} datei={d} markierung={markierung} mitPfad={mitPfad} />
        ))}
      </TableBody>
    </Listentabelle>
  );
}

// ── Startseite ───────────────────────────────────────────────
function Laufwerkkarte({ ordner }: { ordner: FolderView }) {
  const { daten, zahlen, zumOrdner, bestand } = useAblage();
  const { ueber, ziel } = useAblageziel(ordner.id);
  const z = zahlen.get(ordner.id);
  const Zeichen = SICHT_ZEICHEN[wirksameSicht(ordner.id, bestand.folders).visibility];
  const unter = nachkommen(ordner.id, bestand.folders).length;
  return (
    <button
      type="button"
      {...ziel}
      onClick={() => zumOrdner(ordner.id)}
      className={cn(FILE_TILE_CLASS, fileTileState(ueber), "flex flex-col gap-3 p-4")}
    >
      <span className="flex items-center gap-3">
        <span className="bg-bg-secondary text-text-secondary grid h-10 w-10 shrink-0 place-items-center rounded-xl">
          <Zeichen className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="text-heading-sm text-text-primary block truncate">{ordner.name}</span>
          <span className="text-caption text-text-tertiary block tabular-nums">
            {formatDecimal(unter)} Ordner · {formatDecimal(z?.dateien ?? 0)} Dateien · {formatBytes(z?.groesse ?? 0)}
          </span>
        </span>
      </span>
      <span className="text-body-sm text-text-secondary">
        <span className="text-text-primary font-medium">Sehen dürfen:</span> {sehenDuerfen(wirksameSicht(ordner.id, bestand.folders), daten).satz}
      </span>
    </button>
  );
}

export function Startseite() {
  const { bestand, daten, setzen, oeffnen } = useAblage();
  const laufwerke = kinderVon(null, bestand.folders);
  const zuletzt = [...bestand.documents].sort((a, b) => zeitwert(b.updatedAt) - zeitwert(a.updatedAt)).slice(0, 8);
  const links = bestand.documents
    .flatMap((d) => d.shares.filter((l) => linkStatus(l, daten.today) === "aktiv").map((l) => ({ d, l })))
    .sort((a, b) => (a.l.expiresAt ?? "9999").localeCompare(b.l.expiresAt ?? "9999"));

  return (
    <div className="space-y-6 p-4 md:p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {laufwerke.map((f) => (
          <Laufwerkkarte key={f.id} ordner={f} />
        ))}
        <Link href="/dokumente/vorlagen" className={cn(FILE_TILE_CLASS, fileTileState(false), "flex flex-col gap-3 p-4")}>
          <span className="flex items-center gap-3">
            <span className="bg-bg-secondary text-text-secondary grid h-10 w-10 shrink-0 place-items-center rounded-xl">
              <FileStack className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="text-heading-sm text-text-primary block">Vorlagen</span>
              <span className="text-caption text-text-tertiary block tabular-nums">{formatDecimal(bestand.templates.length)} Vordrucke</span>
            </span>
          </span>
          <span className="text-body-sm text-text-secondary">Protokoll, Einladung, Vollmacht — clubhub füllt Vereinsname, Datum und Mitglied aus.</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="overflow-hidden lg:col-span-2">
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <h3 className="text-heading-sm">Zuletzt geändert</h3>
          </div>
          <ul className="divide-border-subtle divide-y pb-1">
            {zuletzt.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => setzen({ ordner: d.folderId, datei: d.id }, true)}
                  className="hover:bg-bg-secondary duration-fast flex w-full items-center gap-3 px-5 py-2.5 text-left transition-colors"
                >
                  <Daumen datei={d} />
                  <span className="min-w-0 flex-1">
                    <span className="text-body-sm text-text-primary block truncate font-medium">{d.name}</span>
                    <span className="text-caption text-text-tertiary block truncate">{pfadText(d.folderId, bestand.folders)}</span>
                  </span>
                  <span className="text-caption text-text-tertiary shrink-0 text-right tabular-nums">
                    {formatDate(d.updatedAt)}
                    <span className="block max-sm:hidden">{d.uploadedBy?.name}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="overflow-hidden">
          <div className="px-5 pt-4 pb-2">
            <h3 className="text-heading-sm">Mit Link geteilt</h3>
            <p className="text-caption text-text-tertiary">Was gerade ohne Zugang abrufbar ist.</p>
          </div>
          {links.length === 0 ? (
            <p className="text-body-sm text-text-tertiary px-5 pb-5">Kein Link aktiv.</p>
          ) : (
            <ul className="divide-border-subtle divide-y pb-1">
              {links.map(({ d, l }) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => setzen({ ordner: d.folderId, datei: d.id }, true)}
                    className="hover:bg-bg-secondary duration-fast flex w-full items-center gap-3 px-5 py-2.5 text-left transition-colors"
                  >
                    <Link2 className="text-text-tertiary h-4 w-4 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="text-body-sm text-text-primary block truncate">{d.name}</span>
                      <span className="text-caption text-text-tertiary block tabular-nums">
                        {l.expiresAt ? `bis ${formatDate(l.expiresAt)}` : "ohne Ablauf"} · {formatDecimal(l.views)} {l.views === 1 ? "Aufruf" : "Aufrufe"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {daten.darfSchreiben && (
            <div className="border-border-subtle border-t px-5 py-3">
              <button
                type="button"
                onClick={() => oeffnen({ art: "ordner-neu", parentId: null })}
                className="text-body-sm text-text-secondary hover:text-text-primary flex items-center gap-2 transition-colors"
              >
                <FolderPlus className="h-4 w-4" aria-hidden />
                Neuen Ordner anlegen
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
